import { usePage } from "@/components/page/PageContext";
import { Button } from "@/components/ui/button";
import { formatLocationType } from "@/lib/format";
import { components } from "@/lib/openapi";
import { deleteRule, getAllRules } from "@/queries/playlists";
import { Trash2 } from "lucide-react";
import React, { useEffect, useState } from "react";

import { DataTable } from "@/components/data-table/data-table";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableToolbar } from "@/components/data-table/data-table-toolbar";
import { CreateRuleButton } from "@/components/playlist/CreateRuleButton";
import { UpdateRuleDialog } from "@/components/playlist/UpdateRuleDialog";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { useDataTable } from "@/hooks/use-data-table";
import { useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Target } from "lucide-react";
import { useMemo } from "react";

type Rule = components["schemas"]["RuleSchema"];

export default function RulesPage() {
  const { isLoading, data: rules = [] } = getAllRules();
  const { setTitles } = usePage();

  useEffect(() => {
    setTitles(["Rules"]);
  }, []);

  const columns = useMemo<ColumnDef<Rule>[]>(
    () => [
      {
        id: "title",
        accessorKey: "title",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Title" />
        ),
        cell: ({ getValue }) => (
          <span className="font-semibold">{getValue() as string}</span>
        ),
        enableSorting: true,
      },
      {
        id: "category",
        accessorKey: "category",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Category" />
        ),
        cell: ({ getValue }) => (
          <span className="text-sm">{getValue() as string}</span>
        ),
        enableSorting: true,
      },
      {
        id: "location_type",
        accessorKey: "location_type",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Location Type" />
        ),
        cell: ({ getValue }) => {
          return (
            <span className="text-sm">
              {formatLocationType(getValue() as string)}
            </span>
          );
        },
        meta: {
          label: "Location Type",
          icon: Target,
        },
        enableSorting: true,
      },
      {
        id: "condition_type",
        accessorKey: "condition_type",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Condition" />
        ),
        cell: ({ getValue }) => {
          const conditionTypeMap: Record<string, string> = {
            above_threshold: "Above Threshold",
            below_threshold: "Below Threshold",
          };
          return (
            <span className="text-sm">
              {conditionTypeMap[getValue() as string] || (getValue() as string)}
            </span>
          );
        },
        enableSorting: true,
      },
      {
        id: "threshold",
        accessorKey: "threshold",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Threshold" />
        ),
        cell: ({ getValue }) => (
          <span className="font-mono">{getValue() as number}</span>
        ),
        enableSorting: true,
      },
      {
        id: "enabled",
        accessorKey: "enabled",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Status" />
        ),
        cell: ({ getValue }) => (
          <Badge variant="secondary">
            {getValue() ? "Enabled" : "Disabled"}
          </Badge>
        ),
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => {
          const rule = row.original;
          return (
            <div className="flex items-center gap-2">
              <UpdateRuleDialog rule={rule} />
              <DeleteRuleButton rule={rule} />
            </div>
          );
        },
        enableSorting: false,
      },
    ],
    []
  );

  const { table } = useDataTable({
    data: rules,
    columns,
    pageCount: 1,
    initialState: {
      sorting: [{ id: "title", desc: false }],
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <span className="text-gray-500">Loading rules...</span>
      </div>
    );
  }

  return (
    <div>
      <DataTable table={table} hidePagination cellClassName="py-3">
        <DataTableToolbar table={table}>
          <CreateRuleButton />
        </DataTableToolbar>
      </DataTable>
    </div>
  );
}

const DeleteRuleButton: React.FC<{ rule: Rule }> = ({ rule }) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const deleteMutation = deleteRule(rule.id);

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync();
      queryClient.invalidateQueries({ queryKey: ["rules"] });
      toast({
        title: "Success",
        description: `Rule "${rule.title}" has been deleted successfully`,
      });
      setIsOpen(false);
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to delete rule",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          disabled={deleteMutation.isPending}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Rule</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete the rule{" "}
            <strong>"{rule.title}"</strong>? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? "Deleting..." : "Delete Rule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
